import React from "react";
import InputViewer from "./InputViewer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@components/ui/tabs";
import { useFullscreenContext } from "../common/withFullscreen";

interface SourceData {
  label: string;
  data: any;
  isConfig?: boolean; // Flag to indicate if this is configuration data
}

interface PaginatedInputViewerProps {
  data?: any;
  label?: string;
  sources?: SourceData[]; // Array of sources for multi-input
  onRefresh?: () => void;
  sourceCount?: number;
  height?: string;
  draggable?: boolean;
  dragDataFormat?: "path" | "expression" | "config";
}

/**
 * PaginatedInputViewer - Reusable wrapper for InputViewer with pagination
 * Provides fixed height container with always-visible header and pagination
 * Supports tabs for multiple input sources
 *
 * Drag format is resolved PER SOURCE:
 *  - Config nodes  → "config"   → {{config.key}}
 *  - All other nodes → dragDataFormat (default "expression") → {{$json.key}}
 */
export const PaginatedInputViewer: React.FC<PaginatedInputViewerProps> = ({
  data,
  label,
  sources,
  onRefresh,
  sourceCount = 0,
  height = "100%",
  draggable = true,
  dragDataFormat = "expression",
}) => {
  const [activeTab, setActiveTab] = React.useState("0");
  const { isFullscreen } = useFullscreenContext();
  const containerHeight = isFullscreen ? "100%" : height;

  // Determine if we're using tabs (multiple sources) or single source
  const useTabs = sources && sources.length > 0;
  const normalizedSources = useTabs
    ? sources
    : data && label
      ? [{ label, data }]
      : [];

  /**
   * Resolve the correct drag format for each individual source.
   *
   * Config node  → "config"          → {{config.api_key}}
   * Other nodes  → dragDataFormat    → {{$json.userId}}
   *
   * Previously a single effectiveDragFormat was computed from ALL sources,
   * so a config node connected alongside a data node would force every tab
   * to use "config" format. Now each tab gets its own format.
   */
  const getSourceDragFormat = React.useCallback(
    (source: SourceData): "path" | "expression" | "config" => {
      // 1. Explicit isConfig flag takes priority
      if (source.isConfig === true) return "config";

      // 2. Fallback: auto-detect presence of config-specific keys in the data object
      const obj = source.data;
      if (obj && typeof obj === "object" && !Array.isArray(obj)) {
        const configKeys = ["uid", "tabId", "configType", "isDecript"];
        if (configKeys.some((key) => key in obj)) return "config";
      }

      // 3. Default to the caller-supplied format (usually "expression")
      return dragDataFormat;
    },
    [dragDataFormat],
  );

  if (normalizedSources.length === 0) {
    return (
      <div
        className="border rounded-md bg-muted/20 nodrag w-full flex flex-col items-center justify-center"
        style={{ height: containerHeight }}
      >
        <div className="text-xs text-muted-foreground">No data available</div>
      </div>
    );
  }

  // ── Single source (no tabs) ──────────────────────────────────────────────
  if (normalizedSources.length === 1) {
    const source = normalizedSources[0];

    // Check if this is a config node
    const dragFormat = getSourceDragFormat(source);
    const isConfigNode = source.isConfig === true || dragFormat === "config";

    // Display "Configuration" for config nodes, otherwise use the original label
    const displayLabel = isConfigNode ? "Configuration" : source.label;

    return (
      <div
        className="border rounded-md bg-muted/20 nodrag w-full flex flex-col overflow-hidden"
        style={{ height: containerHeight }}
      >
        {/* Input Heading */}
        <div className="px-3 pt-2 pb-1 bg-muted/40 border-b flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Input</h3>
            <div className="text-xs text-muted-foreground italic">1 source</div>
          </div>
        </div>

        {/* Node name as single tab */}
        {displayLabel && (
          <div className="px-3 py-2 bg-muted/40 border-b flex-shrink-0">
            <div className="text-xs">
              <strong>{displayLabel}</strong>
            </div>
          </div>
        )}

        <div className="p-0 w-full flex-1 min-h-0">
          <InputViewer
            data={source.data}
            draggable={draggable}
            dragDataFormat={dragFormat} // ← per-source
            showHeader={false}
            sourceCount={sourceCount}
            onRefresh={onRefresh}
          />
        </div>
      </div>
    );
  }

  // ── Multiple sources (tabs) ──────────────────────────────────────────────
  return (
    <div
      className="border rounded-md bg-muted/20 nodrag w-full flex flex-col overflow-hidden"
      style={{ height: containerHeight }}
    >
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col h-full"
      >
        {/* Input Heading */}
        <div className="px-3 pt-2 pb-1 bg-muted/40 border-b flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Input</h3>
            {normalizedSources.length > 0 && (
              <div className="text-sm text-muted-foreground italic">
                {normalizedSources.length} source
                {normalizedSources.length > 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {/* Tabs for connected nodes */}
        <div className="px-2 pt-2 pb-0 bg-muted/40 border-b flex-shrink-0">
          <TabsList className="h-8 bg-transparent p-0 gap-1 pb-2">
            {normalizedSources.map((source, index) => {
              // Check if this is a config node
              const dragFormat = getSourceDragFormat(source);
              const isConfigNode =
                source.isConfig === true || dragFormat === "config";

              // Display "Configuration" for config nodes, otherwise use the original label
              const displayLabel = isConfigNode
                ? "Configuration"
                : source.label;

              return (
                <TabsTrigger
                  key={index}
                  value={String(index)}
                  className="text-[10px] h-7 px-2 data-[state=active]:bg-primary data-[state=active]:text-background data-[state=active]:shadow-sm bg-muted-primary/50 text-muted-foreground rounded-sm bg-muted hover:bg-lightGray"
                >
                  <span className="text-muted-foreground"></span>
                  <strong className="ml-1">{displayLabel}</strong>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          {normalizedSources.map((source, index) => (
            <TabsContent
              key={index}
              value={String(index)}
              className="h-full m-0 p-0 data-[state=inactive]:hidden"
            >
              <div className="h-full">
                <InputViewer
                  data={source.data}
                  draggable={draggable}
                  dragDataFormat={getSourceDragFormat(source)} // ← per-source
                  showHeader={false}
                  sourceCount={sourceCount}
                  onRefresh={onRefresh}
                />
              </div>
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
};

export default React.memo(PaginatedInputViewer);
